import { useEffect, useState } from 'react';

import { t } from 'i18next';

import { CloseFill, SuccessCircleOutline } from '@olist/design-system-icons';
import { Flex } from '@olist/united';

interface IValidationItem {
  label: string;
  valid: boolean;
}

const ValidationItem = ({ label, valid }: IValidationItem) => {
  return (
    <Flex mt="8px">
      {valid ? (
        <SuccessCircleOutline
          data-testid="success-icon"
          style={{ color: '#00865B', marginRight: '2px' }}
        />
      ) : (
        <CloseFill data-testid="close-icon" style={{ color: '#696D8C', marginRight: '2px' }} />
      )}
      <p style={{ color: valid ? '#00865B' : '#696D8C' }}>{label}</p>
    </Flex>
  );
};

interface IPasswordValidator {
  password: string;
  onValid?: Function;
}

export const PasswordValidator: any = ({ onValid, password }: IPasswordValidator) => {
  const validationsInit = {
    special_character: {
      label: t`special characters`,
      valid: false,
    },
    min_characters: {
      label: t`min 12 characters`,
      valid: false,
    },
    min_number: {
      label: t`min 1 number`,
      valid: false,
    },
    min_lowercase: {
      label: t`min 1 lowercase letter`,
      valid: false,
    },
    min_uppercase: {
      label: t`min 1 uppercase letter`,
      valid: false,
    },
  };

  const [validations, setValidations] = useState(validationsInit);

  const checkRules = () => {
    if (!password) {
      return setValidations({
        special_character: {
          ...validations.special_character,
          valid: false,
        },
        min_characters: { ...validations.min_characters, valid: false },
        min_number: { ...validations.min_number, valid: false },
        min_uppercase: { ...validations.min_uppercase, valid: false },
        min_lowercase: { ...validations.min_lowercase, valid: false },
      });
    }

    const matchCharacters = password.length >= 12;
    const matchNumber = !!password.match(/\d/);
    const matchUppercase = !!password.match(/[A-Z]/);
    const matchLowercase = !!password.match(/[a-z]/);
    const matchSpecialCharacter = !!password.match(/\W|_/g);

    const isValidPassword =
      matchCharacters && matchNumber && matchUppercase && matchLowercase && matchSpecialCharacter;

    if (onValid) {
      onValid(isValidPassword);
    }

    return setValidations({
      special_character: {
        ...validations.special_character,
        valid: matchSpecialCharacter,
      },
      min_characters: { ...validations.min_characters, valid: matchCharacters },
      min_number: { ...validations.min_number, valid: matchNumber },
      min_uppercase: { ...validations.min_uppercase, valid: matchUppercase },
      min_lowercase: { ...validations.min_lowercase, valid: matchLowercase },
    });
  };

  useEffect(() => {
    checkRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  return Object.keys(validations).map((key) => (
    <ValidationItem key={key} valid={validations[key].valid} label={validations[key].label} />
  ));
};
